import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetOne, useNotify, useRefresh } from 'react-admin';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import { Funeral } from '../types';
import CppBenefitsReviewModal, {
  CppReviewForm,
} from './CppBenefitsReviewModal';
import CppBenefitsSendModal, { FormEmail } from './CppBenefitsSendModal';
import { useBenefitsStatusRefresh } from './BenefitsStatusField';
import { formMiniState, FORM_MINI_DISPLAY, FormSendSign } from './benefitsStatus';

// One per-form action line under the Benefits headline: the form name (opens
// the review/send modal) plus a compact mini badge showing its own send/sign
// leg (Not sent / Sent / Signed). Data-completeness and mailing are shared
// across both forms and shown by the headline badge, not here.
const FormActionRow: React.FC<{
  label: string;
  form?: FormSendSign;
  onClick?: () => void;
  // When the form doesn't apply to this case (e.g. no surviving spouse) we
  // still show the row — greyed with an N/A badge — so it's clear which
  // benefits are in scope.
  na?: boolean;
}> = ({ label, form, onClick, na }) => {
  if (na || !form || !onClick) {
    return (
      <div className="flex justify-between items-center py-1 pl-3">
        <span className="text-[12px] text-muted-foreground leading-5">{label}</span>
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-400">
          N/A
        </span>
      </div>
    );
  }
  const mini = FORM_MINI_DISPLAY[formMiniState(form)];
  return (
    <div className="flex justify-between items-center py-1 pl-3">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        style={{ color: '#2563eb' }}
        className="text-[12px] hover:underline leading-5 shrink-0 focus:outline-none focus:ring-2 focus:ring-ring rounded text-left"
      >
        {label}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className="inline-flex items-center cursor-pointer rounded hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <span className={'text-[10px] font-medium px-1.5 py-0.5 rounded ' + mini.chipClass}>
          {mini.label}
        </span>
      </button>
    </div>
  );
};

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

type CppFormKey = 'cpp_death_benefit' | 'cpp_survivor_pension';

// Per-form lifecycle state returned by GET cpp-benefits/status/:funeralId.
type FormState = { ready: boolean; sent: boolean; signed: boolean };
export type CppFormStatus = {
  death_benefit: FormState;
  survivor_pension: FormState;
};

const EMPTY_STATE: FormState = { ready: false, sent: false, signed: false };

type CppReviewConfig = { title: string; pdfPath: string; downloadName: string };

const REVIEW_CONFIGS: Record<CppFormKey, CppReviewConfig> = {
  cpp_death_benefit: {
    title: 'CPP Death Benefit',
    pdfPath: 'cpp-benefits/death-benefit-form',
    downloadName: 'CPP_DeathBenefit.pdf',
  },
  cpp_survivor_pension: {
    title: "CPP Survivor's Pension",
    pdfPath: 'cpp-benefits/form',
    downloadName: 'CPP_SurvivorBenefits.pdf',
  },
};

interface Client {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface CppBenefitsSectionProps {
  record: Funeral;
  // The deceased client, already fetched by the parent sidebar rows. Used for
  // the spouse lookup that gates / routes the Survivor's Pension.
  deceasedClient?: any;
  // Same gate the parent uses to decide whether the Survivor's Pension row
  // applies (current spouse / under-18 child / 18-25 student).
  showSurvivorPension: boolean;
}

const contactName = (c?: Client | null) =>
  [c?.first_name, c?.last_name].filter(Boolean).join(' ').trim();

// The CPP benefits block of the funeral sidebar Status section. Owns the two
// form rows (Death Benefit + Survivor's Pension), the review modal (review PDF,
// mark Ready, then Send / Resend / Discard), and the shared Send dialog where
// the agent picks which document(s) to dispatch and to whom. This replaces the
// old TopToolbar "CPP Benefits" button — the sidebar rows are now the single
// entry point.
const CppBenefitsSection: React.FC<CppBenefitsSectionProps> = ({
  record,
  deceasedClient,
  showSurvivorPension,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { bump, setActions } = useBenefitsStatusRefresh();
  const funeralId = record.id as number;

  // The tabbed CPP review modal shows every applicable form at once; this only
  // picks which tab is active when it opens.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewInitial, setReviewInitial] = useState<CppFormKey>('cpp_death_benefit');
  const openReview = useCallback((initial: CppFormKey) => {
    setReviewInitial(initial);
    setReviewOpen(true);
  }, []);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [status, setStatus] = useState<CppFormStatus>({
    death_benefit: EMPTY_STATE,
    survivor_pension: EMPTY_STATE,
  });
  // Forms whose review Send just flattened them — bridges the async gap until
  // loadStatus reflects the new confirmed_at, so the Send dialog lets the agent
  // dispatch the form they just prepared.
  const [preparedForms, setPreparedForms] = useState<Set<CppFormKeyForm>>(new Set());

  // --- Per-form lifecycle status (ready / sent / signed) -------------------
  const loadStatus = useCallback(async () => {
    if (!funeralId) return;
    try {
      const res = await fetch(
        `${PAPERMAN_API_URL}cpp-benefits/status/${funeralId}`,
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            'Refresh-Token': await getRefreshToken(),
          },
        },
      );
      if (!res.ok) return;
      const data = (await res.json()) as CppFormStatus;
      setStatus({
        death_benefit: { ...EMPTY_STATE, ...data?.death_benefit },
        survivor_pension: { ...EMPTY_STATE, ...data?.survivor_pension },
      });
    } catch {
      /* status is best-effort — the rows still work without it */
    }
  }, [funeralId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  // Reload the per-form rows AND signal the headline benefits badge to refetch.
  const reloadStatus = useCallback(() => {
    void loadStatus();
    bump();
  }, [loadStatus, bump]);

  // Register the headline badge's "Ready to send"/"Sent" action: open the review
  // modal for the next applicable form still to be sent (death benefit first,
  // then survivor's pension). The badge stays actionable until every applicable
  // form is out — matching the least-advanced-form headline state.
  const dbState = status.death_benefit;
  const spState = status.survivor_pension;
  useEffect(() => {
    setActions({
      openNextUnsent: () => {
        if (!dbState.sent && !dbState.signed) openReview('cpp_death_benefit');
        else if (showSurvivorPension && !spState.sent && !spState.signed)
          openReview('cpp_survivor_pension');
        else openReview('cpp_death_benefit');
      },
    });
    return () => setActions({});
  }, [
    setActions,
    openReview,
    dbState.sent,
    dbState.signed,
    spState.sent,
    spState.signed,
    showSurvivorPension,
  ]);

  // --- Contacts / recipients ----------------------------------------------
  const { data: payingClient } = useGetOne<Client>(
    'clients',
    { id: (record as any)?.paying_client_id as number },
    { enabled: !!(record as any)?.paying_client_id },
  );
  const { data: authorizedClient } = useGetOne<Client>(
    'clients',
    { id: (record as any)?.authorized_client_id as number },
    { enabled: !!(record as any)?.authorized_client_id },
  );
  const { data: spouseClient } = useGetOne<Client>(
    'clients',
    { id: (deceasedClient as any)?.spouse_id as number },
    { enabled: !!(deceasedClient as any)?.spouse_id },
  );

  // Quick-fill chips for the Send modal — paying client first (most common).
  const sendModalContacts: Array<{ label: string; name: string; email: string }> = [];
  if (payingClient?.email)
    sendModalContacts.push({ label: 'Paying client', name: contactName(payingClient), email: payingClient.email });
  if (authorizedClient?.email && authorizedClient.id !== payingClient?.id)
    sendModalContacts.push({ label: 'Authorized client', name: contactName(authorizedClient), email: authorizedClient.email });
  if (spouseClient?.email)
    sendModalContacts.push({ label: 'Spouse', name: contactName(spouseClient), email: spouseClient.email });

  // The ISP-1200 applicant is the paying client: Section C3 names them, so
  // they sign Section E. No fallback to the authorized client — when the payer
  // has no email the agent picks who receives it (someone helping them sign).
  const sendDefaultEmail = payingClient?.email || '';
  const sendDefaultName = payingClient ? contactName(payingClient) : '';
  const deceasedName = contactName(deceasedClient);

  // Resolve the third-party executor (if any). Mirrors the backend's
  // resolveIsp1200Roles / resolveExecutorSigner: a named executor who is not
  // the paying client signs ISP-1200 Section D2 first.
  const executorInfo = (record as any)?.survivor_benefits_data?.executorInfo || {};
  const executorChoice: string | undefined = executorInfo.executorChoice;
  const someoneElse = executorInfo.someoneElse || {};
  const isPayer = (c?: Client) => !!c && !!payingClient && Number(c.id) === Number(payingClient.id);
  const executorDefaults = ((): { email: string; name: string } => {
    const fromClient = (c?: Client) =>
      c?.email && !isPayer(c) ? { email: c.email, name: contactName(c) } : { email: '', name: '' };
    if (executorChoice === 'authorized') return fromClient(authorizedClient);
    if (executorChoice === 'spouse') return fromClient(spouseClient);
    if (executorChoice === 'someone_else') {
      // "I don't know" names are stored as UNKNOWN — not a name the D2 can print.
      const known = (v?: string) => (v && v !== 'UNKNOWN' ? v : '');
      const name = [known(someoneElse.firstName), known(someoneElse.lastName)].filter(Boolean).join(' ').trim();
      if (someoneElse.email && name) return { email: someoneElse.email, name };
    }
    return { email: '', name: '' };
  })();
  const hasExecutor = !!executorDefaults.email;

  const sendDefaults = {
    isp1200_executor: executorDefaults,
    isp1200_applicant: { email: sendDefaultEmail, name: sendDefaultName },
    isp1300_spouse: {
      // No silent fallback to the applicant — when the spouse has no email the
      // field starts empty so the agent makes a deliberate routing choice.
      email: spouseClient?.email || '',
      name: spouseClient ? contactName(spouseClient) : '',
    },
  };

  // --- Confirmation status (drives the row badge) --------------------------
  const formConfirmations = (record as any)?.form_confirmations || {};
  const dbConfirmed = !!formConfirmations?.cpp_death_benefit?.confirmed_at;
  const spConfirmed = !!formConfirmations?.cpp_survivor_pension?.confirmed_at;

  // --- Send for signature --------------------------------------------------
  const submitSignatureRequest = async (
    payload: {
      isp1200_executor?: FormEmail;
      isp1200_applicant: FormEmail;
      isp1300_spouse?: FormEmail;
    },
    forms: CppFormKeyForm[],
    options?: { copyLinks?: boolean; copyLinkFor?: 'executor' | 'applicant' | 'spouse' },
  ) => {
    if (!funeralId) return;
    setSending(true);
    try {
      const res = await fetch(
        `${PAPERMAN_API_URL}cpp-benefits/create-signing-submission`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getToken()}`,
            'Refresh-Token': await getRefreshToken(),
          },
          body: JSON.stringify({
            funeralId,
            customEmails: payload,
            forms,
            skipEmails: !!options?.copyLinks,
          }),
        },
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.message || `Send failed (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));

      if (options?.copyLinks) {
        const rawBase =
          process.env.REACT_APP_AURA_APP_URL ||
          (typeof window !== 'undefined' && window.location.hostname === 'localhost'
            ? 'http://localhost:3005'
            : 'https://cleocremation.com');
        const trimmed = rawBase.replace(/\/$/, '');
        const auraBase = /\/app$/i.test(trimmed) ? trimmed : `${trimmed}/app`;
        const funeralUuid = (record as any)?.uuid;
        const which = options.copyLinkFor;
        const matches = (sub: any, s: any): boolean => {
          if (!which) return true;
          if (which === 'executor') return s.role === 'executor';
          if (which === 'spouse') return sub.type === 'survivor_pension';
          return sub.type === 'death_benefit' && s.role === 'applicant';
        };
        const lines: string[] = [];
        for (const sub of data?.submissions || []) {
          for (const s of sub?.submitters || []) {
            if (!s?.slug || !matches(sub, s)) continue;
            const label =
              s.role === 'executor'
                ? 'Executor'
                : sub.type === 'survivor_pension'
                  ? 'Spouse'
                  : 'Applicant';
            lines.push(`${label} (${s.email}): ${auraBase}/${funeralUuid}/cpp-benefits/${s.slug}`);
          }
        }
        const text =
          which && lines.length === 1 ? lines[0].replace(/^[^:]+:\s*/, '') : lines.join('\n');
        try {
          await navigator.clipboard.writeText(text);
          notify(
            which
              ? `${which === 'executor' ? 'Executor' : which === 'spouse' ? 'Spouse' : 'Applicant'} signing link copied`
              : 'Signing link(s) copied to clipboard',
            { type: 'success' },
          );
        } catch {
          notify('Could not copy automatically. Links: ' + text, { type: 'info' });
        }
        setSendModalOpen(false);
        reloadStatus();
        refresh();
        return;
      }

      notify('CPP Benefits sent for signature — signer(s) will receive an email', {
        type: 'success',
      });
      setSendModalOpen(false);
      reloadStatus();
      refresh();
    } catch (err: any) {
      console.error('CPP Send error:', err);
      notify(err?.message || 'Failed to send for signature', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  // --- Discard a signed copy so the form can be corrected and resent -------
  const handleDiscardSigned = async (formKey: CppFormKey) => {
    if (!funeralId) return;
    setDiscarding(true);
    try {
      const res = await fetch(
        `${PAPERMAN_API_URL}cpp-benefits/discard-signed/${funeralId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getToken()}`,
            'Refresh-Token': await getRefreshToken(),
          },
          body: JSON.stringify({ formKey }),
        },
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.message || `Discard failed (${res.status})`);
      }
      notify('Signed copy discarded — you can edit and resend the form', {
        type: 'success',
      });
      reloadStatus();
      refresh();
    } catch (err: any) {
      console.error('CPP discard error:', err);
      notify(err?.message || 'Failed to discard signed copy', { type: 'error' });
    } finally {
      setDiscarding(false);
    }
  };

  // The applicable CPP forms, in tab order. Death Benefit always applies;
  // the Survivor's Pension only when there's an eligible survivor.
  const reviewForms: CppReviewForm[] = useMemo(
    () => [
      {
        key: 'cpp_death_benefit',
        ...REVIEW_CONFIGS.cpp_death_benefit,
        confirmed: dbConfirmed,
        sent: status.death_benefit.sent,
        signed: status.death_benefit.signed,
      },
      ...(showSurvivorPension
        ? [
            {
              key: 'cpp_survivor_pension' as CppFormKey,
              ...REVIEW_CONFIGS.cpp_survivor_pension,
              confirmed: spConfirmed,
              sent: status.survivor_pension.sent,
              signed: status.survivor_pension.signed,
            },
          ]
        : []),
    ],
    [
      showSurvivorPension,
      dbConfirmed,
      spConfirmed,
      status.death_benefit.sent,
      status.death_benefit.signed,
      status.survivor_pension.sent,
      status.survivor_pension.signed,
    ],
  );

  // Status handed to the Send dialog, with an optimistic `ready` for any form
  // the agent just prepared this session (see preparedForms).
  const effectiveStatus: CppFormStatus = {
    death_benefit: {
      ...status.death_benefit,
      ready: status.death_benefit.ready || preparedForms.has('death_benefit'),
    },
    survivor_pension: {
      ...status.survivor_pension,
      ready:
        status.survivor_pension.ready || preparedForms.has('survivor_pension'),
    },
  };

  return (
    <>
      <FormActionRow
        label="CPP Death Benefit"
        form={status.death_benefit}
        onClick={() => openReview('cpp_death_benefit')}
      />
      {showSurvivorPension ? (
        <FormActionRow
          label="CPP Survivor's Pension"
          form={status.survivor_pension}
          onClick={() => openReview('cpp_survivor_pension')}
        />
      ) : (
        <FormActionRow label="CPP Survivor's Pension" na />
      )}

      <CppBenefitsReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        funeralId={funeralId}
        forms={reviewForms}
        initialFormKey={reviewInitial}
        discarding={discarding}
        onReadyChange={() => reloadStatus()}
        onPrepared={(preparedKeys) => {
          // The review's Send just flattened every applicable form. Mark them
          // includable in the Send dialog now, without waiting for the async
          // status reload to reflect the new confirmed_at.
          setPreparedForms((prev) => {
            const next = new Set(prev);
            preparedKeys.forEach((k) => next.add(k));
            return next;
          });
          setReviewOpen(false);
          setSendModalOpen(true);
        }}
        onDiscardSigned={(formKey) => handleDiscardSigned(formKey)}
      />

      <CppBenefitsSendModal
        open={sendModalOpen}
        onClose={() => {
          setSendModalOpen(false);
          setPreparedForms(new Set());
        }}
        sending={sending}
        contacts={sendModalContacts}
        deceasedName={deceasedName}
        showSurvivorPension={showSurvivorPension}
        spouseHasEmail={!!spouseClient?.email}
        hasExecutor={hasExecutor}
        formStatus={effectiveStatus}
        defaults={sendDefaults}
        onSend={submitSignatureRequest}
      />
    </>
  );
};

// The set of form keys understood by the send dialog's `forms` payload.
export type CppFormKeyForm = 'death_benefit' | 'survivor_pension';

export default CppBenefitsSection;
